import { FeePostReviewBasisTabProps } from "features/fees/post/FeePostReviewBasisTabProps";
import {
  useFeeChargeEventCreate,
  useFeeChargeEventDelete,
  useFeeChargeEventRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFeeRetrieveSuspense } from "gen/endpoints/fees/fees";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { isEmpty, noop } from "lodash-es";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GenericErrorPageView } from "shared/components/empty/GenericErrorPageView";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageTabs, PageTabsStatus } from "shared/components/layout/PageTabs";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FeePostReviewBasisPage: React.FC = () => {
  const { t } = useTranslation(["fees", "common"]);
  const navigate = useNavigate();

  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();
  const { data: feeChargeEventData } = useFeeChargeEventRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    feeChargeEventData.fee_charge_event.fund_family_event_id!,
    {
      query: {
        enabled: !!feeChargeEventData,
      },
    },
  );

  const { data: feeData } = useFeeRetrieveSuspense(
    fundFamilyId,
    feeChargeEventData.fee_charge_event.fee_id,
  );

  const workflowStatuses = useCeleryWorkflowPoll(
    fundFamilyEventData?.celery_workflow_id
      ? [fundFamilyEventData.celery_workflow_id]
      : undefined,
    () => noop,
    noop,
    noop,
  );

  const { mutateAsync: deleteFeeChargeEvent } = useFeeChargeEventDelete();
  const { mutateAsync: createFeeChargeEvent } = useFeeChargeEventCreate();

  const retryFeeChargeEvent = async () => {
    await deleteFeeChargeEvent({ fundFamilyId, feeChargeEventId });
    const recreatedFeeChargeEvent = await createFeeChargeEvent({
      fundFamilyId,
      data: {
        charged_date_lower: feeChargeEventData.fee_charge_daterange_lower,
        fee_id: feeChargeEventData.fee_charge_event.fee_id,
        fund_family_id: fundFamilyId,
      },
    });
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .fees({})
        .post({ feeChargeEventId: recreatedFeeChargeEvent.id! }).$,
    );
  };

  const tabContext: FeePostReviewBasisTabProps = {
    fundFamilyId,
    feeChargeEventId,
    fundFamilyEventId:
      feeChargeEventData.fee_charge_event.fund_family_event_id!,
  };

  const hiddenTabs: PageTabsStatus = {
    feesPostReviewBasisBreakdown: false,
    feesPostReviewBasisHistoricalTrueUp: isEmpty(feeData.true_up_methodology),
  };

  return fundFamilyEventData?.celery_workflow_id &&
    workflowStatuses.get(fundFamilyEventData.celery_workflow_id)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.failed ? (
    <GenericErrorPageView
      reset={retryFeeChargeEvent}
      error={new Error("celery workflow failed")}
    />
  ) : (
    <PageContent>
      <PageHeader
        size="sm"
        title={t("fees:feeBasisBreakdown.pageTitle")}
        workflowName={t("fees:feeBasisBreakdown.superTitle")}
        badge={{
          color: "default",
          text: feeChargeEventData.fee_charge_daterange_rich_name,
        }}
      />
      <PageBody>
        <PageTabs
          context={tabContext}
          hiddenTabs={hiddenTabs}
          hideSingleTab={true}
        />
        <PageWizardFormFooter />
      </PageBody>
    </PageContent>
  );
};
